<script setup lang="ts">
import { computed, ref } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import {
  ButtonSeverity,
  ButtonSize,
  type DropdownListItem,
  TuDropdownList,
  TuIconButton,
  TuPopover,
  useMobilePopup,
  usePopover,
} from "@tinyurldev/tu-ui-vue";

import type { Invoice } from "@Modules/billing/Data/Models/InvoiceModel";
import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

//
// Props
interface Props {
  invoice: Invoice;
}

const props = defineProps<Props>();

//
// Emit
const emit = defineEmits<{
  (e: "select", type: string): void
}>();

//
// Data
const isMobileView = smallerOrEqual("xl");
const isPopoverVisible = ref(false);
const isMobilePopupVisible = ref(false);

const { referenceRef, popoverRef } = usePopover(isPopoverVisible, {
  placement: "bottom-end",
});

//
// Computed
const dropdownCollection = computed<DropdownListItem[]>(() => {
  return [
    {
      id: "invoice",
      iconClass: "fa-solid fa-file-alt",
      label: __("settings_billing_page.transaction.invoice_download_options.invoice"),
      visible: true,
    },
    {
      id: "receipt",
      iconClass: "fa-solid fa-receipt",
      label: __("settings_billing_page.transaction.invoice_download_options.receipt"),
      visible: props.invoice.hasReceipt(),
    },
  ]
    .filter((option) => option.visible)
    .map(({ visible, ...option }) => ({
      ...option,
      callback: () => selectDownload(option.id),
    }));
});

//
// Methods
function onToggle(): void {
  if (isMobileView.value) {
    isMobilePopupVisible.value = !isMobilePopupVisible.value;
  } else {
    isPopoverVisible.value = !isPopoverVisible.value;
  }
}

/**
 * Closes the menu before telling the parent view what to download, so the menu is
 * never left open behind the full page navigation the download triggers.
 */
function selectDownload(type: string): void {
  onToggle();
  emit("select", type);
}

useMobilePopup(() => ({
  component: TuDropdownList,
  props: { collection: dropdownCollection.value },
}), isMobilePopupVisible);

//
// Exposed Test Vars
defineExpose({ dropdownCollection, isPopoverVisible, isMobilePopupVisible });
</script>

<template>
  <div class="download-invoice">
    <TuIconButton
      ref="referenceRef"
      :aria-label="__('settings_billing_page.transaction.download_invoice_label')"
      is-outlined
      :severity="ButtonSeverity.PRIMARY"
      :size="ButtonSize.MEDIUM"
      icon-class="fa-solid fa-download"
      class="download-invoice__ref-button _download_invoice"
      @click="onToggle"
    />

    <TuPopover
      v-show="isPopoverVisible"
      ref="popoverRef"
    >
      <TuDropdownList :collection="dropdownCollection" />
    </TuPopover>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.download-invoice {
  &__ref-button {
    border-radius: border.$small-radius;
  }
}
</style>
